import { isPlainObject } from 'lodash';

import type { QueryClient, QueryKey } from '@tanstack/react-query';

export type PlainRecord = Record<string, unknown>;
export type OptimisticMutationMeta = {
  optimistic?: string;
  invalidate?: string[];
  /** Product action recorded as a Sentry breadcrumb when the mutation succeeds, e.g. `email.sent`. */
  track?: string;
};
export type OptimisticPatch = {
  id: string;
  patch: PlainRecord | ((current: PlainRecord) => PlainRecord) | null;
};
export type CacheSnapshot = { queryKey: QueryKey; data: unknown };

const isRecord = (value: unknown): value is PlainRecord => isPlainObject(value);
const DROP = Symbol('drop');

const rewrite = (node: unknown, id: string, patch: OptimisticPatch['patch']): unknown => {
  if (Array.isArray(node)) {
    let changed = false;
    const next = node.flatMap((item) => {
      const rewritten = rewrite(item, id, patch);
      changed ||= rewritten !== item;

      return rewritten === DROP ? [] : [rewritten];
    });

    return changed ? next : node;
  }

  if (!isRecord(node)) {
    return node;
  }

  if (node.id === id) {
    if (patch === null) {
      return DROP;
    }

    return { ...node, ...(typeof patch === 'function' ? patch(node) : patch) };
  }

  let changed = false;
  const next: PlainRecord = {};

  for (const [key, value] of Object.entries(node)) {
    const rewritten = rewrite(value, id, patch);
    next[key] = rewritten === DROP ? value : rewritten;
    changed ||= next[key] !== value;
  }

  return changed ? next : node;
};

export const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

let optimisticIdSequence = 0;

export const optimisticId = (prefix: string) =>
  `optimistic-${prefix}-${(optimisticIdSequence += 1)}`;

const isSnapshot = (value: unknown): value is CacheSnapshot =>
  isRecord(value) && Array.isArray(value.queryKey);

export const patchRecordInCache = (
  queryClient: QueryClient,
  id: string,
  patch: OptimisticPatch['patch'],
): CacheSnapshot[] => {
  const updates = queryClient
    .getQueryCache()
    .getAll()
    .map((query) => {
      const next = rewrite(query.state.data, id, patch);

      return { query, previous: query.state.data, next: next === DROP ? undefined : next };
    })
    .filter(({ previous, next }) => next !== previous);

  if (!updates.length) {
    return [];
  }

  const hashes = new Set(updates.map(({ query }) => query.queryHash));
  queryClient.cancelQueries({ predicate: (query) => hashes.has(query.queryHash) });

  for (const { query, next } of updates) {
    queryClient.setQueryData(query.queryKey, next);
  }

  return updates.map(({ query, previous }) => ({ queryKey: query.queryKey, data: previous }));
};

export const rollbackCacheSnapshots = (queryClient: QueryClient, snapshots: unknown) => {
  const cache = queryClient.getQueryCache();

  for (const { queryKey, data } of asArray(snapshots).filter(isSnapshot).reverse()) {
    if (cache.find({ queryKey, exact: true })) {
      queryClient.setQueryData(queryKey, data);
    }
  }
};

const resolvePatch = (
  variables: unknown,
  meta?: OptimisticMutationMeta,
): OptimisticPatch | undefined => {
  const { optimistic } = meta ?? {};
  if (!optimistic || !variables || typeof variables !== 'object') {
    return undefined;
  }

  const id = (variables as PlainRecord)[optimistic];
  const body = (variables as { data?: unknown }).data;
  if (typeof id !== 'string' || !isRecord(body)) {
    return undefined;
  }

  const patch = Object.fromEntries(Object.entries(body).filter(([, value]) => value !== undefined));

  return { id, patch };
};

export const applyOptimisticPatch = (
  queryClient: QueryClient,
  variables: unknown,
  meta?: OptimisticMutationMeta,
): CacheSnapshot[] => {
  const resolved = resolvePatch(variables, meta);
  return resolved ? patchRecordInCache(queryClient, resolved.id, resolved.patch) : [];
};

export const applyServerResponseToCache = (
  queryClient: QueryClient,
  variables: unknown,
  response: unknown,
  meta?: OptimisticMutationMeta,
) => {
  const resolved = resolvePatch(variables, meta);
  if (!resolved) {
    return;
  }

  const envelope = (response as { data?: unknown })?.data;
  const record = isRecord(envelope) && 'data' in envelope ? envelope.data : envelope;
  if (isRecord(record) && record.id === resolved.id) {
    patchRecordInCache(queryClient, record.id, record);
  }
};
